import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";

/*
 * Authentication/Apollo integration lives in AuthenticatedFeatureAccess.
 * This provider remains independently testable through its injected loader.
 *
 * identity is null or { tenantId, objectId }, used only to bind responses to
 * the active account, never as authorization. The injected stable loader is
 * loadAccess({ identity, signal, reason }) ->
 * { tenantId, objectId, permissions: string[], version: string }.
 * It must reject transport/GraphQL/partial-data/store failures, use no-cache,
 * honor AbortSignal and enforce a request deadline. Future getMyAccess asks
 * for the verified principal with NO caller-selected identity arguments.
 *
 * IDs, versions and permissions are exact nonblank/unpadded strings. Versions
 * are opaque snapshots, not a claim of freshness or a replay-defense mechanism.
 * There is no wildcard/admin expansion or profile/email-based permission.
 * An empty permission response is valid; an empty guard requirement is not.
 *
 * Manual refresh withdraws grants and supersedes pending work. Visible focus,
 * pageshow and visibility revalidation coalesce while pending and start at most
 * once per 30 seconds. Integration must also invalidate after API permission
 * denials, clear restricted-data caches on account changes, and keep the API
 * authoritative. A client guard cannot cancel already authorized server work.
 * Put protected effects/fetches INSIDE guarded children, never in their parent.
 */

const EMPTY_PERMISSIONS = Object.freeze([]);
const ACCESS_ERROR = Object.freeze({ code: "FEATURE_ACCESS_UNAVAILABLE" });
const REVALIDATION_INTERVAL_MS = 30000;
const AccessContext = createContext(null);
const NO_ACCESS = Object.freeze({
  status: "unauthenticated",
  identity: null,
  permissions: EMPTY_PERMISSIONS,
  version: null,
  verification: null,
  error: null,
  refresh: () => false,
  hasPermissions: () => false,
});

const owns = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const isIdentifier = (value) => typeof value === "string" && value.length > 0 && value.trim() === value;

function validPermissions(value, allowEmpty = false) {
  // Array.from exposes sparse entries which Array.every alone would skip.
  return Array.isArray(value) && (allowEmpty || value.length > 0) && Array.from(value).every(isIdentifier);
}

function emptyState(scope, status) {
  return {
    scope,
    status,
    permissions: EMPTY_PERMISSIONS,
    version: null,
    verification: null,
    error: status === "error" ? ACCESS_ERROR : null,
  };
}

function readAccess(result, identity) {
  if (
    !isRecord(result) ||
    !["tenantId", "objectId", "permissions", "version"].every((key) => owns(result, key)) ||
    result.tenantId !== identity.tenantId ||
    result.objectId !== identity.objectId ||
    !validPermissions(result.permissions, true) ||
    !isIdentifier(result.version)
  ) {
    throw new Error("Invalid feature access response.");
  }
  return {
    permissions: Object.freeze(Array.from(result.permissions)),
    version: result.version,
  };
}

export function FeatureAccessProvider({ identity = null, loadAccess, children }) {
  const signedOut = identity === null;
  const validIdentity =
    isRecord(identity) &&
    ["tenantId", "objectId"].every((key) => owns(identity, key) && isIdentifier(identity[key]));
  const tenantId = validIdentity ? identity.tenantId : null;
  const objectId = validIdentity ? identity.objectId : null;
  const scope = useMemo(
    () => ({
      identity: validIdentity ? Object.freeze({ tenantId, objectId }) : null,
      loadAccess,
      status: signedOut
        ? "unauthenticated"
        : !validIdentity || typeof loadAccess !== "function"
          ? "error"
          : "loading",
    }),
    [signedOut, validIdentity, tenantId, objectId, loadAccess],
  );
  const [state, setState] = useState(null);
  const activeSession = useRef(null);

  useEffect(() => {
    if (scope.status !== "loading") {
      return undefined;
    }

    let mounted = true;
    let generation = 0;
    let controller = null;
    let pending = false;
    let lastStarted = -Infinity;

    function run(reason, automatic = false) {
      if (!mounted) {
        return false;
      }
      const now = Date.now();
      if (automatic && (pending || (now >= lastStarted && now - lastStarted < REVALIDATION_INTERVAL_MS))) {
        return false;
      }
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      const requestGeneration = ++generation;
      pending = true;
      lastStarted = now;
      setState(emptyState(scope, "loading"));

      async function load() {
        try {
          const result = await scope.loadAccess({ identity: scope.identity, signal, reason });
          if (!mounted || signal.aborted || requestGeneration !== generation) {
            return;
          }
          const grants = readAccess(result, scope.identity);
          pending = false;
          setState({ ...emptyState(scope, "ready"), ...grants, verification: requestGeneration });
        } catch {
          if (!mounted || signal.aborted || requestGeneration !== generation) {
            return;
          }
          pending = false;
          // Raw transport/store errors can contain credentials and must stay private.
          setState(emptyState(scope, "error"));
        }
      }
      void load();
      return true;
    }

    const session = { scope, run };
    activeSession.current = session;
    run("initial");

    function revalidate(event) {
      if (document.visibilityState !== "hidden") {
        run(event.type === "visibilitychange" ? "visibility" : event.type, true);
      }
    }
    window.addEventListener("focus", revalidate);
    window.addEventListener("pageshow", revalidate);
    document.addEventListener("visibilitychange", revalidate);

    return () => {
      mounted = false;
      generation += 1;
      controller?.abort();
      window.removeEventListener("focus", revalidate);
      window.removeEventListener("pageshow", revalidate);
      document.removeEventListener("visibilitychange", revalidate);
      if (activeSession.current === session) {
        activeSession.current = null;
      }
    };
  }, [scope]);

  const refresh = useCallback(() => {
    const session = activeSession.current;
    return session?.scope === scope ? session.run("refresh") : false;
  }, [scope]);

  const value = useMemo(() => {
    // Deny during render, before passive cleanup can run after an account/loader
    // change. Returning to an old account creates a new scope, not a cache hit.
    const snapshot = state?.scope === scope ? state : emptyState(scope, scope.status);
    return {
      status: snapshot.status,
      identity: scope.identity,
      permissions: snapshot.permissions,
      version: snapshot.version,
      // Local lifecycle only, not server freshness or authorization evidence.
      // Also invalidates protected reads if a fast same-version recheck batches
      // away its intermediate loading render.
      verification: snapshot.verification,
      error: snapshot.error,
      refresh,
      hasPermissions: (requiredPermissions) =>
        snapshot.status === "ready" &&
        validPermissions(requiredPermissions) &&
        Array.from(requiredPermissions).every((permission) => snapshot.permissions.includes(permission)),
    };
  }, [scope, state, refresh]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useFeatureAccess() {
  return useContext(AccessContext) || NO_ACCESS;
}

export function FeatureAccessGuard({ requiredPermissions, fallback, children }) {
  const access = useFeatureAccess();
  if (access.hasPermissions(requiredPermissions)) {
    return <>{children}</>;
  }
  // Explicit null supports action guards. A fallback must contain only public UI.
  if (fallback !== undefined) {
    return <>{fallback}</>;
  }
  if (access.status === "loading") {
    return (
      <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} aria-hidden="true" />
        <Typography>Checking feature access.</Typography>
      </Box>
    );
  }
  if (access.status === "unauthenticated") {
    return <Alert severity="info">Sign in to access this feature.</Alert>;
  }
  if (access.status === "error") {
    return (
      <Alert
        severity="error"
        action={<Button color="inherit" size="small" onClick={access.refresh}>Retry</Button>}
      >
        Feature access could not be verified.
      </Alert>
    );
  }
  return <Alert severity="warning">You do not have permission to access this feature.</Alert>;
}